import { useState } from 'react'
import Image from 'next/image'
import { propOr } from 'lodash/fp'
import { isMobileOnly } from 'react-device-detect'
import Link from 'next/link'

import { clickRatesAndFees } from 'clients/segment'
import {
  CallToApply,
  CardArt,
  LinkToRedirect,
  StarRatingEditor,
} from 'components/'
import { useClientGeoLocation } from 'components/app/clientGeoLocationContext'
import { CreditDisclosure } from 'components/credit-disclosure'
import { ProductDetails } from 'components/entity-panel/sections/components/product-details'
import { ApplyNowIcon } from 'components/link-to-redirect/ApplyNowIcon'
import { SecureSite } from 'components/secure-site'
import { firstIsVowel, nextImageHelper, supify } from 'utils/'
import type { CompareCredit } from '../../../types/compare-credit'

export const CardTile: React.FC<{
  orderId: string
  card: CompareCredit.FormattedCard
  externalId: string | undefined
  highlight?: string | null
  highlightDescription?: string | null
  ribbon?: string | null
  ribbonText?: string | null
}> = ({
  orderId,
  card,
  externalId = '00',
  highlight,
  highlightDescription,
  ribbon,
  ribbonText,
}) => {
  const { country } = useClientGeoLocation()

  const [expandDetails, setExpandDetails] = useState(false)
  const handleExpandDetails = () => setExpandDetails(!expandDetails)

  return (
    <div
      className={`c-card-tile / relative / flex flex-col md:flex-row md:flex-wrap / sm:-mx-6 md:-mx-8 / px-4 xxs:px-6 xs:px-8 lg:px-10 / pt-6 pb-1 / leading-normal / rounded border-2 ${
        ribbon === 'tetriary' ? 'border-tetriary' : ''
      } ${ribbon === 'primary-bright' ? 'border-primary-bright' : ''} ${
        ribbon === 'none' ? 'border-slate-200' : ''
      }`}
    >
      {ribbon === 'none' ? (
        ''
      ) : (
        <div
          className={`absolute / top-0 left-0 / -ml-2 xs:-ml-3 -mt-4 / pl-4 pr-8 / text-base md:text-lg font-bold ${
            ribbon === 'tetriary' ? 'c-ribbon-bg-rt text-primary' : ''
          } ${
            ribbon === 'primary-bright'
              ? 'c-ribbon-bg-rt c-ribbon-bg-rt--primary-bright text-white'
              : ''
          }`}
        >
          {ribbonText}
        </div>
      )}

      <div className="c-card-tile__col-1 / w-full md:w-1/3 lg:w-28pct / mx-auto mb-4">
        <h3 className="sm:w-96 / mx-auto mb-0.5 / md:hidden">
          <LinkToRedirect
            externalId={externalId}
            linkParams={card.applyNowLinkParameters}
            orderId={orderId}
            query={undefined}
            slug={card.slug}
          >
            <div
              className="inline-block / w-full / text-center text-primary-mid leading-snug font-bold xs:text-lg sm:text-xl / hover:text-primary-bright cursor-pointer / transition-all"
              dangerouslySetInnerHTML={{
                __html: supify(card.name),
              }}
            />
          </LinkToRedirect>
        </h3>
        <div></div>
        <div className="c-card-art / w-36 xs:w-40 sm:w-48 / text-center / flex items-center justify-center shadow-lg / mx-auto mb-3">
          <div className="relative / w-full / rounded-sm shadow">
            <LinkToRedirect
              externalId={externalId}
              linkParams={card.applyNowLinkParameters}
              orderId={orderId}
              query={undefined}
              slug={card.slug}
            >
              <div className="relative flex rounded-sm overflow-hidden / fs_card-tile_card-art">
                <span className="c-card-art__content / absolute / flex flex-wrap content-center items-center / z-10 left-0 / w-full h-full text-white">
                  <span className="w-6 h-6 / mx-auto">
                    <ApplyNowIcon
                      className="inline-block mb-1"
                      width={24}
                      height={24}
                      aria-hidden={true}
                    />
                  </span>
                  <span className="w-full font-semibold text-sm sm:text-base">
                    Apply Now
                  </span>
                </span>

                <CardArt
                  cardArt={card.cardArt}
                  imgClasses="relative / z-0 / w-full h-full object-cover top-0"
                  customCodeSnippets={card.customCodeSnippets}
                  issuer={card.issuer.slug.current}
                  name={card.name}
                  orderId={orderId}
                  slug={card.slug}
                  type={card._type}
                  categoryId="80"
                  externalId="80"
                  _rev={null}
                />
              </div>
            </LinkToRedirect>
          </div>
        </div>

        <div className="flex items-center justify-center / w-full">
          <StarRatingEditor
            productSlug={card.slug}
            stars={card.expertReviewRating}
          />
        </div>
        <div className="w-full / mx-auto mb-4 / md:hidden">
          <LinkToRedirect
            externalId={externalId}
            linkParams={card.applyNowLinkParameters}
            orderId={orderId}
            query={undefined}
            slug={card.slug}
          >
            <div className="c-btn c-btn--primary max-w-none text-lg mb-2 / fs_card-tile_cta">
              Apply Now
              <span className="w-4 h-4 / ml-2">
                <ApplyNowIcon width={16} height={16} className="lazyload" />
              </span>
            </div>
          </LinkToRedirect>
          <p className="text-fs13 text-slate-600 / text-center leading-tight / mb-2">
            <SecureSite
              issuerName={card.issuer.name}
              issuerSecureSite={card.issuerSecureSite}
            />
          </p>
          <div className="c-card-tile__call">
            {card._type === 'card' &&
              card.phoneNumber &&
              isMobileOnly &&
              country === 'US' && (
                <div className="w-full / pt-2">
                  <p className="mb-3">
                    <CallToApply classes="max-w-xs" entity={card} />
                  </p>

                  <p className="text-xs text-slate-600 / text-center / -mt-1 mb-4 / md:self-start w-full max-w-xs leading-4">
                    with {firstIsVowel(card.issuer.name) ? 'an' : 'a'}{' '}
                    {card.issuer.name} representative
                  </p>
                </div>
              )}
          </div>
          {card._type === 'card' &&
            card.ratesAndFees &&
            card.ratesAndFees !== 'N/A' && (
              <p className="text-primary-bright-dark text-xs w-full text-center mb-2 / hover:text-primary-bright">
                <Link
                  href={card.ratesAndFees}
                  onClick={() => {
                    clickRatesAndFees({
                      applyNowLink: card.applyNowLink,
                      component: 'Card Tips Summary',
                      name: card.name,
                      url: window.location.href,
                    })
                  }}
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  Rates &amp; Fees
                </Link>
              </p>
            )}
        </div>

        <div className="relative z-1 / max-w-xs w-56 xs:w-72 md:w-full / mx-auto mb-1 / p-1 / text-fs13 text-center / rounded bg-primary-light-bg">
          <p className="mb-0.5 tracking-wider text-fs10 leading-snug uppercase">
            Recommended Credit
          </p>
          <span className="flex items-start justify-center leading-snug">
            {card.creditNeeded && (
              <span className="c-compare__rec-credit-icon / flex-shrink-0 / w-7 mr-1.5 / -mt-1.5 / inline-block">
                {propOr('', 'creditNeeded', card)
                  .toLowerCase()
                  .startsWith('excellent') && (
                  <Image
                    alt="excellent credit"
                    height={12}
                    width={12}
                    src={`/static/icons/credit-quality/excellent.svg`}
                    style={nextImageHelper.responsive}
                    sizes="100vw"
                  />
                )}
                {propOr('', 'creditNeeded', card)
                  .toLowerCase()
                  .startsWith('good') && (
                  <Image
                    alt="good credit"
                    height={12}
                    width={12}
                    src={`/static/icons/credit-quality/good.svg`}
                    style={nextImageHelper.responsive}
                    sizes="100vw"
                  />
                )}
                {propOr('', 'creditNeeded', card)
                  .toLowerCase()
                  .startsWith('average') && (
                  <Image
                    alt="fair credit"
                    height={12}
                    width={12}
                    src={`/static/icons/credit-quality/fair.svg`}
                    style={nextImageHelper.responsive}
                    sizes="100vw"
                  />
                )}
                {propOr('', 'creditNeeded', card)
                  .toLowerCase()
                  .startsWith('fair') && (
                  <Image
                    alt="fair credit"
                    height={12}
                    width={12}
                    src={`/static/icons/credit-quality/fair.svg`}
                    style={nextImageHelper.responsive}
                    sizes="100vw"
                  />
                )}
                {propOr('', 'creditNeeded', card)
                  .toLowerCase()
                  .startsWith('bad') ||
                  (propOr('', 'creditNeeded', card)
                    .toLowerCase()
                    .startsWith('poor') && (
                    <Image
                      alt="poor credit"
                      height={12}
                      width={12}
                      src={`/static/icons/credit-quality/poor.svg`}
                      style={nextImageHelper.responsive}
                      sizes="100vw"
                    />
                  ))}
                {propOr('', 'creditNeeded', card)
                  .toLowerCase()
                  .startsWith('limited') && (
                  <Image
                    alt="limited credit"
                    height={12}
                    width={12}
                    src={`/static/icons/credit-quality/limited.svg`}
                    style={nextImageHelper.responsive}
                    sizes="100vw"
                  />
                )}
              </span>
            )}
            <span className="tracking-wide font-semibold text-primary">
              {card.creditNeeded || 'N/A'}
            </span>
            <CreditDisclosure theme="t-card-tile" id={card.slug} />
          </span>
        </div>
        <button
          className="flex flex-row items-center justify-center / w-full / py-2 / text-sm text-center text-slate-500 font-semibold / hover:text-primary-mid / hidden md:flex"
          onClick={handleExpandDetails}
          aria-label={`${expandDetails ? 'Show' : 'Hide'} summary`}
          aria-expanded={expandDetails ? false : true}
          aria-controls={`card-detail-${card.slug}`}
        >
          Card Details{' '}
          <span className={`inline-block w-3 ml-2 opacity-40`}>
            <Image
              alt="expand"
              src={
                expandDetails
                  ? '/static/icons/icon-minus-black.svg'
                  : '/static/icons/icon-plus-black.svg'
              }
              height={24}
              width={24}
              aria-hidden={true}
              style={nextImageHelper.responsive}
              sizes="100vw"
            />
          </span>
        </button>
      </div>
      <div className="c-card-tile__col-2 / md:w-2/3 lg:w-72pct / md:mb-1.5 / md:pl-10 py-4 md:py-0 / border-t border-b md:border-none border-slate-200">
        <h3 className="md:pr-4 / hidden md:block">
          <LinkToRedirect
            externalId={externalId}
            linkParams={card.applyNowLinkParameters}
            orderId={orderId}
            query={undefined}
            slug={card.slug}
          >
            <div
              className="inline-block / w-full mb-3 / lg:pr-16 / text-primary-mid leading-snug font-bold text-xl md:text-2xl / hover:text-primary-bright cursor-pointer / transition-all"
              dangerouslySetInnerHTML={{
                __html: supify(card.name),
              }}
            />
          </LinkToRedirect>
        </h3>
        <p className="mb-3 / font-bold text-primary leading-snug">
          <span>Best For:</span> {highlight}
        </p>
        <p className="md:mb-6 / text-base">{highlightDescription}</p>
        <div className="w-64 hidden md:block">
          <LinkToRedirect
            externalId={externalId}
            linkParams={card.applyNowLinkParameters}
            orderId={orderId}
            query={undefined}
            slug={card.slug}
          >
            <div className="c-btn c-btn--primary max-w-none text-lg mb-2 / fs_card-tile_cta">
              Apply Now
              <span className="w-4 h-4 / ml-2">
                <ApplyNowIcon width={16} height={16} className="lazyload" />
              </span>
            </div>
          </LinkToRedirect>
          <p className="text-fs13 text-slate-600 / text-center leading-tight / mb-2">
            <SecureSite
              issuerName={card.issuer.name}
              issuerSecureSite={card.issuerSecureSite}
            />
          </p>
          <div className="c-card-tile__call">
            {card._type === 'card' &&
              card.phoneNumber &&
              isMobileOnly &&
              country === 'US' && (
                <div className="w-full / pt-2">
                  <p className="mb-3">
                    <p className="mb-3">
                      <CallToApply classes="max-w-xs" entity={card} />
                    </p>
                  </p>

                  <p className="text-xs text-slate-600 / text-center / -mt-1 mb-4 / md:self-start w-full max-w-xs leading-4">
                    with {firstIsVowel(card.issuer.name) ? 'an' : 'a'}{' '}
                    {card.issuer.name} representative
                  </p>
                </div>
              )}
          </div>
          {card._type === 'card' &&
            card.ratesAndFees &&
            card.ratesAndFees !== 'N/A' && (
              <p className="text-primary-bright-dark text-xs w-full text-center mb-2 hover:text-primary-bright">
                <Link
                  href={card.ratesAndFees}
                  onClick={() => {
                    clickRatesAndFees({
                      applyNowLink: card.applyNowLink,
                      component: 'Card Tips Summary Detail',
                      name: card.name,
                      url: window.location.href,
                    })
                  }}
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  Rates &amp; Fees
                </Link>
              </p>
            )}
        </div>
      </div>
      <div>
        <button
          className="flex flex-row items-center justify-center / w-full / py-2 / text-sm text-center text-slate-500 font-semibold / hover:text-primary-mid / md:hidden"
          onClick={handleExpandDetails}
          aria-label={`${expandDetails ? 'Show' : 'Hide'} summary`}
          aria-expanded={expandDetails ? false : true}
          aria-controls={`card-detail-${card.slug}`}
        >
          {expandDetails ? 'Show Less' : 'Expand Details'}
          <span className={`inline-block w-3 ml-2 opacity-40`}>
            <Image
              alt="expand"
              src={
                expandDetails
                  ? '/static/icons/icon-minus-black.svg'
                  : '/static/icons/icon-plus-black.svg'
              }
              height={24}
              width={24}
              aria-hidden={true}
              style={nextImageHelper.responsive}
              sizes="100vw"
            />
          </span>
        </button>
        <div
          id={`card-detail-${card.slug}`}
          className={`c-slidedown leading-normal text-slate-700 ${
            expandDetails ? 'md:pt-4 md:pb-2' : 'c-slidedown--hidden'
          } md:border-t md:border-slate-200`}
        >
          <p className="mb-1 uppercase font-bold text-sm text-primary tracking-wide">
            Product Details
          </p>
          <ProductDetails externalId="80" orderId={orderId} entity={card} />
        </div>
      </div>
    </div>
  )
}
